import { useMemo } from "react";

import { QuestionPreviewNext } from "@vericus/cadmus-common";

import { CadmusAIPill } from "@/features/marking/components/student-prompt/cadmus-ai-pill";
import { QuestionFragment, QuestionType } from "@/generated/graphql";

import * as styles from "./sheet-preview.css";
import {
  getQuestionPreviewDataAndPromptDoc,
  usePreviewShuffleSeed,
} from "./utils";

interface Props {
  questionTitle: string;
  question: QuestionFragment;
  points: number | null;
  isChildQuestion: boolean;
  correctResponseMarking: boolean;
  socraticQuestionerEnabled: boolean;
  /** Whether to shuffle the question's answer choices */
  shuffle?: boolean | null;
  /** Open the rubric linked to the question. Omit to hide the rubric button. */
  onOpenRubric?: () => void;
}

/**
 * Render a Task's question in a preview only component.
 */
export function TaskBlockPreview(props: Props) {
  const {
    question,
    questionTitle,
    isChildQuestion,
    points,
    correctResponseMarking,
  } = props;

  const shuffleSeed = usePreviewShuffleSeed(props.shuffle);

  const { promptDoc, questionData } = useMemo(
    () =>
      question
        ? getQuestionPreviewDataAndPromptDoc(question, points, shuffleSeed)
        : { promptDoc: null, questionData: null },
    [question, points, shuffleSeed]
  );

  if (!question || !questionData) return null;

  const previewMode = correctResponseMarking ? "response" : "question";
  const automarkDisplay = correctResponseMarking ? "badge" : undefined;

  return (
    <div className={styles.questionBlock}>
      <QuestionPreviewNext
        questionId={question.id}
        data={questionData}
        questionTitle={questionTitle}
        withHeader={question.questionType !== QuestionType.Overview}
        promptDoc={promptDoc}
        className={isChildQuestion ? styles.childquestion : undefined}
        mode={previewMode}
        automarkDisplay={automarkDisplay}
        onRubricClick={props.onOpenRubric}
      />
      <div style={{ padding: "0 45px" }}>
        {props.socraticQuestionerEnabled && <CadmusAIPill />}
      </div>
    </div>
  );
}
